import { Link, useNavigate, useParams } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { apiClient, apiErrorMessage } from '@/lib/api'
import { useAuth } from '@/stores/authStore'

type InvitePreview =
  | { status: 'pending'; workspaceName: string; email: string; expiresAt: string }
  | { status: 'accepted'; workspaceName: string; email: string; acceptedAt: string }
  | { status: 'revoked' | 'expired' | 'invalid'; workspaceName?: string; email?: string; message?: string }

export default function InvitePage() {
  const { token = '' } = useParams()
  const { user } = useAuth()
  const navigate = useNavigate()
  const qc = useQueryClient()

  const preview = useQuery<InvitePreview>({
    queryKey: ['invite', token],
    queryFn: async () =>
      (await apiClient.get<InvitePreview>(`/api/v1/workspace/invite/${token}`)).data,
    enabled: Boolean(token),
    retry: false,
  })

  const acceptMut = useMutation({
    mutationFn: async () => {
      return (await apiClient.post<{ success: boolean; workspace: { id: string; name: string } }>(
        `/api/v1/workspace/invite/${token}/accept`
      )).data
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['session'] })
      navigate('/dashboard', { replace: true })
    },
  })

  if (preview.isLoading) return <p className="p-8 text-sm text-neutral-500">Loading…</p>
  const d = preview.data
  if (!d)
    return (
      <div className="p-8 max-w-md">
        <h1 className="text-xl font-semibold">Invalid invite</h1>
        <p className="mt-2 text-sm text-neutral-600">{apiErrorMessage(preview.error)}</p>
      </div>
    )

  return (
    <div className="mx-auto max-w-md p-8 rounded border bg-white">
      <h1 className="text-xl font-semibold">
        {'workspaceName' in d && d.workspaceName
          ? `Join ${d.workspaceName}`
          : 'Workspace invite'}
      </h1>
      {'email' in d && d.email && (
        <p className="mt-2 text-sm text-neutral-600">For: {d.email}</p>
      )}

      {d.status === 'pending' && (
        <>
          {user ? (
            <>
              <button
                onClick={() => acceptMut.mutate()}
                disabled={acceptMut.isPending}
                className="mt-4 w-full rounded bg-neutral-900 text-white text-sm py-2 disabled:opacity-50"
              >
                {acceptMut.isPending ? 'Accepting…' : 'Accept invite'}
              </button>
              {acceptMut.error && (
                <p className="mt-2 text-xs text-red-600">{apiErrorMessage(acceptMut.error)}</p>
              )}
            </>
          ) : (
            <p className="mt-4 text-sm">
              <Link to="/login" className="underline">Sign in</Link>
              {' '}or{' '}
              <Link to="/register" className="underline">register</Link>
              {' '}with this email to accept.
            </p>
          )}
        </>
      )}

      {d.status !== 'pending' && (
        <p className="mt-4 text-sm text-neutral-600">
          {'message' in d && d.message ? d.message : `Invite status: ${d.status}`}
        </p>
      )}
    </div>
  )
}
